import { Suspense } from "react";
import { Switch, Route, Router as WouterRouter } from "wouter";
import { useBrowserLocation } from "wouter/use-browser-location";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ScrollToTop } from "@/components/ScrollToTop";
import CookieBanner from "@/components/CookieBanner";
import { lazyPage } from "@/lib/lazyPage";

// Eager: Home is the most-visited route and the primary LCP target, so we
// keep it in the main bundle to avoid an extra round-trip on initial load.
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";

// Code-split everything else. Each lazyPage() produces its own JS chunk and
// is preloaded by the prerender step so static HTML is fully populated.
const WhatWeDo = lazyPage(() => import("@/pages/WhatWeDo"));
const AiData = lazyPage(() => import("@/pages/AiData"));
const D365 = lazyPage(() => import("@/pages/D365"));
const Automation = lazyPage(() => import("@/pages/Automation"));
const UpliftX = lazyPage(() => import("@/pages/UpliftX"));
const Industries = lazyPage(() => import("@/pages/Industries"));
const AgedCare = lazyPage(() => import("@/pages/AgedCare"));
const Approach = lazyPage(() => import("@/pages/Approach"));
const About = lazyPage(() => import("@/pages/About"));
const OurName = lazyPage(() => import("@/pages/OurName"));
const News = lazyPage(() => import("@/pages/News"));
const BlogPost = lazyPage(() => import("@/pages/BlogPost"));
const Contact = lazyPage(() => import("@/pages/Contact"));
const Privacy = lazyPage(() => import("@/pages/Privacy"));
const Terms = lazyPage(() => import("@/pages/Terms"));

// Each industry detail page composes IndustryDetailV2 with one content blob.
// Wrapping the pair inside a single lazyPage() keeps the IndustryDetailV2
// renderer + the matching content blob in the same chunk, and out of the
// main bundle entirely.
const Healthcare = lazyPage(async () => {
  const [{ default: IndustryDetailV2 }, { HEALTHCARE }] = await Promise.all([
    import("@/pages/IndustryDetailV2"),
    import("@/pages/industries-v2-content"),
  ]);
  return { default: () => <IndustryDetailV2 content={HEALTHCARE} /> };
});
const Ndis = lazyPage(async () => {
  const [{ default: IndustryDetailV2 }, { NDIS }] = await Promise.all([
    import("@/pages/IndustryDetailV2"),
    import("@/pages/industries-v2-content"),
  ]);
  return { default: () => <IndustryDetailV2 content={NDIS} /> };
});
const Education = lazyPage(async () => {
  const [{ default: IndustryDetailV2 }, { EDUCATION }] = await Promise.all([
    import("@/pages/IndustryDetailV2"),
    import("@/pages/industries-v2-content"),
  ]);
  return { default: () => <IndustryDetailV2 content={EDUCATION} /> };
});
const Government = lazyPage(async () => {
  const [{ default: IndustryDetailV2 }, { GOVERNMENT }] = await Promise.all([
    import("@/pages/IndustryDetailV2"),
    import("@/pages/industries-v2-content"),
  ]);
  return { default: () => <IndustryDetailV2 content={GOVERNMENT} /> };
});
const FinancialServices = lazyPage(async () => {
  const [{ default: IndustryDetailV2 }, { FINANCIAL_SERVICES }] =
    await Promise.all([
      import("@/pages/IndustryDetailV2"),
      import("@/pages/industries-v2-content"),
    ]);
  return { default: () => <IndustryDetailV2 content={FINANCIAL_SERVICES} /> };
});

/**
 * Preload every code-split route. Called by the prerender step so that
 * `renderToString` produces complete HTML for every page (no Suspense
 * fallbacks). Safe to call multiple times — each lazyPage caches its
 * loader promise.
 */
export async function preloadAllPages(): Promise<void> {
  await Promise.all([
    WhatWeDo.preload(),
    AiData.preload(),
    D365.preload(),
    Automation.preload(),
    UpliftX.preload(),
    Industries.preload(),
    AgedCare.preload(),
    Approach.preload(),
    About.preload(),
    OurName.preload(),
    News.preload(),
    BlogPost.preload(),
    Contact.preload(),
    Privacy.preload(),
    Terms.preload(),
    Healthcare.preload(),
    Ndis.preload(),
    Education.preload(),
    Government.preload(),
    FinancialServices.preload(),
  ]);
}

function AppRoutes() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/what-we-do" component={WhatWeDo} />
      <Route path="/what-we-do/ai-data" component={AiData} />
      <Route path="/what-we-do/d365" component={D365} />
      <Route path="/what-we-do/automation" component={Automation} />
      <Route path="/what-we-do/upliftx" component={UpliftX} />
      <Route path="/industries" component={Industries} />
      <Route path="/industries/aged-care" component={AgedCare} />
      <Route path="/industries/healthcare" component={Healthcare} />
      <Route path="/industries/ndis" component={Ndis} />
      <Route path="/industries/education" component={Education} />
      <Route path="/industries/government" component={Government} />
      <Route path="/industries/financial-services" component={FinancialServices} />
      <Route path="/approach" component={Approach} />
      <Route path="/about" component={About} />
      <Route path="/our-name" component={OurName} />
      <Route path="/news" component={News} />
      <Route path="/news/:slug" component={BlogPost} />
      <Route path="/contact" component={Contact} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route component={NotFound} />
    </Switch>
  );
}

interface AppProps {
  ssrPath?: string;
}

function stripTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

const useTrailingSlashSafeLocation = (): [string, (path: string, ...args: any[]) => any] => {
  const [loc, navigate] = useBrowserLocation();
  return [stripTrailingSlash(loc), navigate];
};

function App({ ssrPath }: AppProps = {}) {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const normalizedSsrPath = ssrPath ? stripTrailingSlash(ssrPath) : ssrPath;
  const isBrowser = typeof window !== "undefined";
  const routerProps = isBrowser
    ? { base, ssrPath: normalizedSsrPath, hook: useTrailingSlashSafeLocation }
    : { base, ssrPath: normalizedSsrPath };
  return (
    <WouterRouter {...routerProps}>
      <ScrollToTop />
      <SiteHeader />
      <main className="page-fade">
        {/* Suspense boundary for client-side route transitions while a
            lazy chunk is in flight. Fallback is null because the previous
            page stays visible until the new one is ready. */}
        <Suspense fallback={null}>
          <AppRoutes />
        </Suspense>
      </main>
      <SiteFooter />
      <CookieBanner />
    </WouterRouter>
  );
}

export default App;
